import React from 'react';
import autobind from 'autobind';
import { inject, observer } from 'mobx-react';
import uniqBy from 'lodash/uniqBy';

import { EntryTypeStatus } from 'chaika-frontend-types/frontend-cms';

import { Ehr } from '@patient/store/ehr/Ehr';
import { AuthUser } from '@store/AuthUser/AuthUser';
import { EntryPage as EntryPageStore } from '@patient/ui/shared/store';
import { EntryTypeList } from '@template/store/entryType/EntryTypeList';
import { Entry } from '@patient/store/entry/Entry';
import { entryReservationService } from '@patient/services/entry/entryReservationService';
import { OptionType } from '@components/Select';
import { AppState } from '@store/App/App';

import { ViewPortState } from '@store/ViewPort';
import { authUserService } from '@store/AuthUser/services/authUserService';
import { isDefined } from '@utils/isDefined';
import { EditEntryData, EditEntryDataProps } from './EditEntryData';
import { translate } from '@chaika/i18n/helper';

export interface Props extends EditEntryDataProps {
    onEntryTypeSelect(typeId: string, typeVersion: number): void;
}

interface EntryTypeOption extends OptionType<string, { typeVersion: number }> {}

interface StoreProps {
    app: AppState;
    ehr: Ehr;
    authUser: AuthUser;
    entry: Entry;
    entryTypeList: EntryTypeList;
    entryPage: EntryPageStore;
    viewPort: ViewPortState;
}

const injectableStores: (keyof StoreProps)[] = [
    AppState.Name,
    Ehr.Name,
    EntryTypeList.Name,
    Entry.Name,
    AuthUser.Name,
    ViewPortState.Name,
];

@observer
class Component extends React.Component<Props & StoreProps> {
    public async componentDidMount(): Promise<void> {
        await authUserService.loadEntryTypes();
    }

    public render(): React.ReactNode {
        const {
            entry,
            ehr,
            viewPort: { isMobileSize },
        } = this.props;

        const entryTypeOptions = this.makeEntryTypeOptions();

        const selectedEntryType = entry.entryType;

        if (entry.entryType && selectedEntryType.status !== EntryTypeStatus.Active) {
            entryTypeOptions.push({
                value: `${selectedEntryType.id}_${selectedEntryType.version}`,
                label: selectedEntryType.title,
                typeVersion: selectedEntryType.version ?? 0,
                isDisabled: true,
            });
        } else if (entry.entryType && !entryTypeOptions.find(({ value }) => value === selectedEntryType.id)) {
            entryTypeOptions.push({
                value: selectedEntryType.id,
                label: selectedEntryType.title,
                typeVersion: selectedEntryType.version ?? 0,
            });
        }

        const withConsent =
            entry.reservation && ehr.hasInformedConsent(entry.reservation.id, entry.reservation.serviceIds[0]);

        return React.createElement(EditEntryData, {
            entryTypeOptions,
            isMobile: isMobileSize,
            informedConsentSigned:
                withConsent && entry.reservation
                    ? ehr.isInformedConsentSigned(entry.reservation.id, entry.reservation.serviceIds[0])
                    : undefined,
            disableChangeEntryType: selectedEntryType.isSystem,
            reservationId: entry.reservation?.id ?? null,
            onReservationSelect: this.handleReservationSelect,
            reservationOptions: entryReservationService.getReservationsOptions(),
            isSigned: entry.isSigned,
            entryTypeName: selectedEntryType ? selectedEntryType.title : '',
            isEntryViewMode: entry.isViewMode,
            selectedEntryTypeId:
                entry.entryType && selectedEntryType.status === EntryTypeStatus.Active
                    ? entry.typeId
                    : `${entry.typeId}_${entry.typeVersion}`,
            onEntryTypeSelect: this.handleEntryTypeSelect,
            loadEntryTypeOptions: this.loadEntryTypeOptions,
            hasEDS: isDefined(entry.eds),
        });
    }

    @autobind
    private async loadEntryTypeOptions(query: string): Promise<OptionType<string>[]> {
        const formattedQuery = query.toLocaleLowerCase().trim();
        const legalEntityId = authUserService.getSelectedOrganizationLegalEntityId();

        const legalEntityEntryTypes = this.props.entryTypeList.activeEntryTypes.filter(
            item =>
                !item.legalEntityIds ||
                item.legalEntityIds.length === 0 ||
                (legalEntityId && item.legalEntityIds.includes(legalEntityId)),
        );

        return legalEntityEntryTypes
            .map(entryType => ({
                value: entryType.id,
                label: entryType.title,
                typeVersion: entryType.version,
            }))
            .filter(({ label }) => label.toLocaleLowerCase().includes(formattedQuery));
    }

    @autobind
    private async handleReservationSelect({ value }: OptionType<string>): Promise<void> {
        const { app } = this.props;

        app.isOnUpdate = true;
        try {
            await entryReservationService.updateEntryReservation(value);
        } catch (e: any) {
            app.showingPopupParams = {
                title: translate('failLinkReservationToEntry'),
                description: e.message,
            };
        }

        app.isOnUpdate = false;
    }

    @autobind
    private async handleEntryTypeSelect({ value, typeVersion }: EntryTypeOption) {
        await this.props.onEntryTypeSelect(value, typeVersion);
    }

    private makeEntryTypeOptions(): EntryTypeOption[] {
        const { authUser, entryTypeList } = this.props;
        const defaultEntryTypeOptions = entryTypeList.values
            .filter(it => it.isDefault)
            .map(it => ({
                value: it.id,
                label: it.title,
                typeVersion: it.version ?? 0,
            }));

        const authUserEntryTypeOptions = authUser.userEntryTypes.map(entryType => ({
            value: entryType.id,
            label: entryType.title,
            typeVersion: entryType.version,
        }));

        return uniqBy([...defaultEntryTypeOptions, ...authUserEntryTypeOptions], param => param.value);
    }
}

export const EditEntryDataContainer = inject<Props, StoreProps>(...injectableStores)(Component);
